import React from 'react';
import styles from 'components/transactionsShuttle/TransactionsShuttle.module.css';
import { MockService, PriorityMode } from 'types/rest/mockServiceTypes';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';
import { PanePosition, ShuttlePaneControls } from 'store/transactionShuttle/TransactionShuttleTypes';
import TransactionShuttlePane from 'components/transactionsShuttle/TransactionShuttlePane';
import { Transaction, TransactionType } from 'types/rest/assetCatalogTypes';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import NoContentPlaceholder from 'components/ui/noContentPlaceholder/NoContentPlaceholder';
import { useTranslation } from 'react-i18next';
import BzmIcon from '../icons/BzmIcon';
import { SelectAllType } from '../blazeTable/BlazeTable.types';
import { AssignmentActionType } from './TransactionShuttle.types';
import _ from 'lodash';
import { SortConfiguration } from '../../types/rest/paginationTypes';

type TransactionsShuttleStatelessProps = {
  context: MockService | MockServiceTemplate;
  disabled: boolean;
  onUpdateTransaction: (type: PanePosition, transaction: Transaction, afterSaveCallback?: any) => void;
  isMoveLeftInProgress: boolean;
  isMoveRightInProgress: boolean;
  leftPaneTransactions: Transaction[];
  rightPaneTransactions: Transaction[];
  leftPaneControls: ShuttlePaneControls;
  rightPaneControls: ShuttlePaneControls;
  leftPaneTitle: any;
  rightPaneTitle: any;
  changeActivePageInPane: (type: PanePosition, activePage: number) => void;
  filterChangedInPane: (type: PanePosition, searchTerm: string) => void;
  txnSelectionChangedInPane: (type: PanePosition, transactionIds: number[], selectAllType?: SelectAllType) => void;
  expandItem: (type: PanePosition, item: number | null) => void;
  reloadHandler: () => void;
  moveTransactions: (assignMentType: AssignmentActionType, txId?: number) => void;
  isSearchDisabled: boolean;
  onSort: (type: PanePosition, sort: SortConfiguration) => void;
  onPriorityModeChanged?: (priorityMode: PriorityMode) => void;
  onUpdateShowPriorityModalVisibility?: (isShown: boolean) => void;
  transactionType: TransactionType;
};

/**
 * Stateless transaction shuttle. All data and configuration are coming from outside through props.
 */
const TransactionsShuttle = (props: TransactionsShuttleStatelessProps) => {
  const { t } = useTranslation('mockServices');
  const rightButton = (
    <>
      <button
        className="btn btn-primary btn-square btn-lg mb-4"
        onClick={() => props.moveTransactions(AssignmentActionType.ADD)}
        disabled={
          (props.leftPaneControls.selected.length === 0 && props.leftPaneControls.selectAll !== 'ALL') ||
          props.isMoveLeftInProgress
        }
      >
        {props.isMoveLeftInProgress ? (
          <SimpleLoader loadingText={''} style={{ position: 'relative', top: '-1px', left: '-1px' }} />
        ) : (
          <BzmIcon variant={'icon-bzm-chevron-right'} className={'icon-sm'} />
        )}
      </button>
    </>
  );

  const leftButton = (
    <button
      className="btn btn-primary btn-square btn-lg"
      onClick={() => props.moveTransactions(AssignmentActionType.REMOVE)}
      disabled={
        (props.rightPaneControls.selected.length === 0 && props.rightPaneControls.selectAll !== 'ALL') ||
        props.isMoveRightInProgress
      }
    >
      {props.isMoveRightInProgress ? (
        <SimpleLoader loadingText={''} style={{ position: 'relative', top: '-1px', left: '-1px' }} />
      ) : (
        <BzmIcon variant={'icon-bzm-chevron-left'} className={'icon-sm'} />
      )}
    </button>
  );

  const noResultsPlaceHolder = (side: PanePosition, leftPaneWithContent: boolean) => {
    // left side text, same whether right side has transactions or not
    let titleLine1 = t('transactions:txnTable.blankLeftTitle1');
    let titleLine2 = t('transactions:txnTable.blankLeftTitle2');
    let line1 = t('transactions:txnTable.blankLeftText');
    if (side === 'RIGHT') {
      titleLine1 = t('transactions:txnTable.blankRightTitle1');
      titleLine2 = t('transactions:txnTable.blankRightTitle2');
      if (leftPaneWithContent) {
        // right side text when there is transaction on left side
        line1 = t('transactions:txnTable.blankRightTextA');
      } else {
        // right side text when there is no transactions whatsoever
        line1 = t('transactions:txnTable.blankRightTextB');
      }
    }
    return (
      <Row className={'justify-content-center'}>
        <Col>
          <Row className={'justify-content-center'}>
            <h2>
              {titleLine1}
              <br />
              {titleLine2}
            </h2>
          </Row>
          <Row>
            <p className={'mb-1'} dangerouslySetInnerHTML={{ __html: line1 }}></p>
          </Row>
        </Col>
      </Row>
    );
  };

  const getPlaceholder = (side: PanePosition) => {
    return (
      <div style={{ top: side === 'LEFT' ? 0 : 125 }} className={'position-relative'}>
        <NoContentPlaceholder
          placeHolderContent={noResultsPlaceHolder(side, props.leftPaneTransactions.length > 0)}
          height={350}
          iconSize={'60px'}
        />
      </div>
    );
  };

  return (
    <div className={'d-flex flex-nowrap'}>
      <TransactionShuttlePane
        context={props.context}
        disabled={props.disabled}
        isDropzoneVisible={true}
        paneState={props.leftPaneControls}
        showTxnPriority={false}
        title={props.leftPaneTitle}
        transactions={props.leftPaneTransactions}
        reloadHandler={props.reloadHandler}
        updateActivePage={(pageNumber: number) => props.changeActivePageInPane('LEFT', pageNumber)}
        filterChanged={(searchTerm: string) => props.filterChangedInPane('LEFT', searchTerm)}
        selectTransactions={(transactions: number[], selectAll) =>
          props.txnSelectionChangedInPane('LEFT', transactions, selectAll)
        }
        expandItem={(item: number | null) => props.expandItem('LEFT', item)}
        isSearchDisabled={props.isSearchDisabled}
        tableClassName="ms-left-panel"
        updateTransaction={transaction => props.onUpdateTransaction('LEFT', transaction)}
        noResultPlaceholder={t('transactions:txnTable.noTransactionsFound')}
        emptyPlaceholder={getPlaceholder('LEFT')}
        paneType={'LEFT'}
        moveTransactions={props.moveTransactions}
        sort={_.isNil(props.leftPaneControls.sort) ? ({} as SortConfiguration) : props.leftPaneControls.sort}
        onSort={sort => {
          props.onSort('LEFT', sort);
        }}
        transactionType={props.transactionType}
      />

      <div className={`align-self-center ${styles.shuttleButtonsSection}`}>
        {rightButton}
        {leftButton}
      </div>

      <TransactionShuttlePane
        context={props.context}
        disabled={props.disabled}
        isDropzoneVisible={false}
        paneState={props.rightPaneControls}
        showTxnPriority={props.context.priorityMode === 'DEFAULT'}
        title={props.rightPaneTitle}
        transactions={props.rightPaneTransactions}
        updateActivePage={(pageNumber: number) => props.changeActivePageInPane('RIGHT', pageNumber)}
        filterChanged={(searchTerm: string) => props.filterChangedInPane('RIGHT', searchTerm)}
        selectTransactions={(transactions: number[], selectAll) =>
          props.txnSelectionChangedInPane('RIGHT', transactions, selectAll)
        }
        expandItem={(item: number | null) => props.expandItem('RIGHT', item)}
        reloadHandler={props.reloadHandler}
        isSearchDisabled={props.isSearchDisabled}
        tableClassName="ms-right-panel"
        updateTransaction={transaction => props.onUpdateTransaction('RIGHT', transaction)}
        noResultPlaceholder={t('transactions:txnTable.noTransactionsFound')}
        emptyPlaceholder={getPlaceholder('RIGHT')}
        paneType={'RIGHT'}
        moveTransactions={props.moveTransactions}
        sort={_.isNil(props.rightPaneControls.sort) ? ({} as SortConfiguration) : props.rightPaneControls.sort}
        onSort={sort => {
          props.onSort('RIGHT', sort);
        }}
        onPriorityModeChanged={props.onPriorityModeChanged}
        priorityMode={_.get(props.context, 'priorityMode')}
        onUpdateShowPriorityModal={props.onUpdateShowPriorityModalVisibility}
        transactionType={props.transactionType}
      />
    </div>
  );
};

export default TransactionsShuttle;
